import React from "react";
import {
  Input,
  InputNumber,
  Select,
  Checkbox,
  DatePicker,
  Upload,
  Button,Image
} from "antd";
import { UploadOutlined,DeleteOutlined } from "@ant-design/icons";

const LabelWrap = React.memo(({ name, computedLabel, required, children }) => {
  return (
    <div className="flex flex-col mb-3">
      {computedLabel && (
        <label
          htmlFor={name}
          className="text-[11px] font-medium text-gray-600 dark:text-gray-400 tracking-wide mb-1 uppercase"
        >
          {computedLabel}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
    </div>
  );
});

const Field = ({ fieldInfo = {}, value, onChange }) => {
  const {
    type,
    label,
    name,
    placeholder,
    required,
    disabled,
    readOnly,
    options = [],
    rows = 3,
    accept,
    min,
    max
  } = fieldInfo || {};

  const computedLabel = label ?? name ?? "";
  const safeValue = value === undefined ? "" : value;

  const baseProps = {
    id: name,
    placeholder,
    disabled,
    readOnly,
  };

  const wrapOnChange = (handler) => {
    if (!handler) return undefined;
    return (e) => {
      if (e && e.target !== undefined) handler(e.target.value);
      else handler(e);
    };
  };

  // === RENDER TYPES ===

  if (type === "text") {
    return (
      <LabelWrap name={name} computedLabel={computedLabel} required={required}>
        <Input
          {...baseProps}
          value={safeValue}
          onChange={wrapOnChange(onChange)}
          className="text-sm rounded-md focus:border-blue-500 focus:ring-0"
        />
      </LabelWrap>
    );
  }

  if (type === "number" || type === "float") {
    return (
      <LabelWrap name={name} computedLabel={computedLabel} required={required}>
        <InputNumber
          {...baseProps}
          value={safeValue === "" ? undefined : safeValue}
          onChange={onChange}
          min={min || 0}
          max={max || 999999999999}
          className="w-full text-sm rounded-md focus:border-blue-500 focus:ring-0"
        />
      </LabelWrap>
    );
  }

  if (type === "textarea") {
    return (
      <LabelWrap name={name} computedLabel={computedLabel} required={required}>
        <Input.TextArea
          {...baseProps}
          rows={rows}
          value={safeValue}
          onChange={wrapOnChange(onChange)}
          className="text-sm rounded-md focus:border-blue-500 focus:ring-0"
        />
      </LabelWrap>
    );
  }

  if (type === "date") {
    return (
      <LabelWrap name={name} computedLabel={computedLabel} required={required}>
        <DatePicker
          {...baseProps}
          style={{ width: "100%" }}
          className="text-sm rounded-md focus:border-blue-500 focus:ring-0"
          value={safeValue || null}
          onChange={(date, dateString) => onChange(dateString)}
        />
      </LabelWrap>
    );
  }

  if (type === "select") {
    return (
      <LabelWrap name={name} computedLabel={computedLabel} required={required}>
        <Select
          {...baseProps}
          value={safeValue === "" ? undefined : safeValue}
          options={options}
          onChange={(val) => onChange(val)}
          allowClear={!required}
          className="text-sm"
        />
      </LabelWrap>
    );
  }

  if (type === "checkbox") {
    return (
      <div className="flex items-center space-x-2 mb-3">
        <Checkbox
          checked={!!value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 uppercase">
          {computedLabel}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </span>
      </div>
    );
  }

  if (type === "color") {
    return (
      <LabelWrap name={name} computedLabel={computedLabel} required={required}>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value || "#000000"}
            onChange={(e) => onChange(e.target.value)}
            className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
          />
          <Input
            value={value || "#000000"}
            onChange={wrapOnChange(onChange)}
            className="w-28 text-sm rounded-md"
          />
        </div>
      </LabelWrap>
    );
  }

// ensure Image, Upload, Button, UploadOutlined, DeleteOutlined are imported
// make sure Image, Upload, Button, UploadOutlined, DeleteOutlined are imported
if (type === "file" || type === "image") {
  const isImg = type === "image";
  const hasValue = !!value;

  return (
    <LabelWrap name={name} computedLabel={computedLabel} required={required}>
      <div className="flex items-center gap-3">
        {/* Directly pass value to Image when present (no transformations) */}
        {hasValue && (
          <Image
            src={value}
            alt="preview"
            preview={false}
            fallback=""
            style={{
              width: 56,
              height: 56,
              objectFit: "cover",
              borderRadius: 6,
              border: "1px solid #e5e7eb",
            }}
          />
        )}

        {/* Upload / Change button (do NOT pass `value` into Upload) */}
        <Upload
          beforeUpload={
            (file) => {
              const maxSizeMB=1
  const isOk = file.size / 1024 / 1024 < maxSizeMB;
  if (!isOk) {
    message.error(`File must be smaller than ${maxSizeMB}MB`);
    return Upload.LIST_IGNORE; // or return false
  }
  return true;
}
          }
          showUploadList={false}
          accept={accept || (isImg ? "image/*" : undefined)}

          onChange={(info) => {
            const file = info?.fileList?.[0]?.originFileObj;
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => onChange(reader.result); // you already want value set
            reader.readAsDataURL(file);
          }}
        >
          <Button
            icon={<UploadOutlined />}
            size="middle"
            className="text-xs sm:text-sm rounded-md border-gray-300 hover:border-blue-500 hover:text-blue-600"
          >
            {hasValue ? (isImg ? "Change Logo" : "Change File") : `Upload ${computedLabel}`}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </Button>
        </Upload>

        {/* Remove button */}
        {hasValue && (
          <Button
            icon={<DeleteOutlined />}
            size="middle"
            type="text"
            className="text-xs sm:text-sm text-red-500"
            onClick={() => onChange("")}
          />
        )}
      </div>
    </LabelWrap>
  );
}




  // fallback (text)
  return (
    <LabelWrap name={name} computedLabel={computedLabel} required={required}>
      <Input
        {...baseProps}
        value={safeValue}
        onChange={wrapOnChange(onChange)}
        className="text-sm rounded-md focus:border-blue-500 focus:ring-0"
      />
    </LabelWrap>
  );
};

export { Field };
